import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, Lock, Download, Share2 } from "lucide-react";
import { fmtINR } from "@/lib/api";
import { downloadQr, shareQr } from "@/lib/qrUtils";
import api from "@/lib/api";
import AntiScreenshotQR from "@/components/AntiScreenshotQR";

/**
 * Full-screen QR zoom modal — same temporal-multiplexed canvas
 * rendered at large size for easy scanning. Includes:
 *  - Amount + merchant header (so a friend paying on someone's
 *    behalf knows what they're paying for at a glance)
 *  - In-modal Download + Share actions (gated on merchant
 *    `qr_allow_download` and not in screenshot-shield/hold mode)
 *  - Web Share API integration so customers can drop the QR
 *    straight into WhatsApp / Telegram / Signal / etc.
 * Honours the merchant's hold-to-view setting (cover + reveal
 * catcher) inside the zoom too. Closes on backdrop, ESC, or X.
 *
 * Extracted from PaymentPage.jsx (was ~200 LOC inline).
 */
export default function QrZoomModal({
    data,
    screenshotShield,
    qrHoldMode,
    qrRevealing,
    setQrRevealing,
    onClose,
    setData,
}) {
    // Track in-flight share/download so we can disable buttons & show a tiny
    // spinner instead of letting the customer fire the canvas pipeline twice.
    const [qrActionBusy, setQrActionBusy] = useState(null); // null | "download" | "share"
    // Toast shown after a successful share/download from inside the zoom modal.
    const [qrShareNote, setQrShareNote] = useState("");

    // Auto-clear the confirmation toast after a short delay.
    useEffect(() => {
        if (!qrShareNote) return;
        const t = setTimeout(() => setQrShareNote(""), 2500);
        return () => clearTimeout(t);
    }, [qrShareNote]);

    // ESC closes zoom modal.
    useEffect(() => {
        const onEsc = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onEsc);
        return () => document.removeEventListener("keydown", onEsc);
    }, [onClose]);

    const applySessionExtend = (ext, note) => {
        if (ext?.extended && ext.new_expires_at) {
            setData((d) => ({ ...d,
                expires_at: ext.new_expires_at,
                session_extended: true }));
            setQrShareNote(`+${ext.extended_minutes}m added · ${note}`);
        } else {
            setQrShareNote(note);
        }
    };

    const handleDownload = async () => {
        setQrActionBusy("download");
        // Fire telemetry AND check for an auto session-extend on the response.
        const tele = api.post(
            `/public/payment-page/${data.txn_id}/qr-shared`,
            { kind: "download" },
        ).catch(() => null);
        try {
            await downloadQr(data);
            const r = await tele;
            applySessionExtend(r?.data, "QR downloaded");
        } finally { setQrActionBusy(null); }
    };

    const handleShare = async () => {
        setQrActionBusy("share");
        // Share is the more interesting signal — strong proxy for
        // "third-party will pay on my behalf".
        const tele = api.post(
            `/public/payment-page/${data.txn_id}/qr-shared`,
            { kind: "share" },
        ).catch(() => null);
        try {
            const shared = await shareQr(data);
            const r = await tele;
            applySessionExtend(r?.data, shared ? "Opened share sheet" : "QR downloaded");
        } finally { setQrActionBusy(null); }
    };

    return (
        <div
            data-testid="qr-zoom-modal"
            role="dialog"
            aria-modal="true"
            onClick={onClose}
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/85 px-4 py-6 backdrop-blur-sm animate-in fade-in duration-150"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                onContextMenu={(e) => e.preventDefault()}
                className="bg-white rounded-2xl shadow-2xl max-w-[92vw] w-[380px] flex flex-col items-center pay-watermark relative pay-protected animate-in zoom-in-95 duration-200"
                data-watermark={`${data.txn_id} · ${data.username}\n${new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: false })}`}
            >
                <button
                    data-testid="qr-zoom-close"
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="absolute -top-2.5 -right-2.5 w-9 h-9 rounded-full bg-slate-900 text-white shadow-lg flex items-center justify-center hover:bg-slate-700 active:scale-95 transition-all text-xl leading-none font-bold z-10"
                >×</button>

                {/* Amount + merchant header — large hierarchy so it
                    reads as "Pay ₹X to MERCHANT" from across a room. */}
                <div className="w-full px-6 pt-6 pb-3 text-center border-b border-slate-100">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Scan to pay</div>
                    <div data-testid="qr-zoom-amount" className="text-3xl sm:text-4xl font-bold text-slate-900 mt-1 tracking-tight">
                        {fmtINR(data.amount)}
                    </div>
                    <div className="text-[12px] sm:text-[13px] text-slate-500 mt-0.5 truncate">
                        to <span className="font-medium text-slate-700">{data.partner_name || data.snapshot?.display_name || "Merchant"}</span>
                    </div>
                </div>

                {/* QR card */}
                <div className="px-6 py-5 flex flex-col items-center">
                    <div className="relative bg-white p-3 rounded-xl ring-1 ring-slate-200 shadow-sm">
                        {data.qr_anti_screenshot === false ? (
                            <img
                                src={data.qr_image}
                                alt="QR enlarged"
                                className={`w-[260px] h-[260px] sm:w-[300px] sm:h-[300px] ${screenshotShield ? "opacity-0" : ""}`}
                                draggable={false}
                                onContextMenu={(e) => e.preventDefault()}
                            />
                        ) : (
                            <AntiScreenshotQR
                                src={data.qr_image}
                                size={300}
                                shielded={screenshotShield}
                                alt="QR enlarged"
                                className="w-[260px] h-[260px] sm:w-[300px] sm:h-[300px]"
                            />
                        )}
                        {qrHoldMode && !qrRevealing && !screenshotShield && (
                            <div
                                data-testid="qr-zoom-hold-cover"
                                onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); setQrRevealing(true); }}
                                onContextMenu={(e) => e.preventDefault()}
                                className="absolute inset-3 flex flex-col items-center justify-center bg-slate-900/95 rounded-lg ring-2 ring-amber-400 px-2 text-center cursor-pointer select-none"
                                style={{ WebkitUserSelect: "none", userSelect: "none", WebkitTouchCallout: "none", touchAction: "none" }}
                            >
                                <Lock className="w-10 h-10 text-amber-300 mb-2" />
                                <div className="text-base font-bold text-amber-200">Hold to view QR</div>
                                <div className="text-xs text-amber-100/80 mt-1">Release to hide</div>
                            </div>
                        )}
                        {qrHoldMode && qrRevealing && (
                            <div
                                data-testid="qr-zoom-reveal-catcher"
                                onPointerUp={() => setQrRevealing(false)}
                                onPointerLeave={() => setQrRevealing(false)}
                                onPointerCancel={() => setQrRevealing(false)}
                                onContextMenu={(e) => e.preventDefault()}
                                className="absolute inset-3 rounded-lg cursor-grab"
                                style={{ touchAction: "none", background: "transparent" }}
                            />
                        )}
                        {screenshotShield && (
                            <div className="absolute inset-3 flex flex-col items-center justify-center bg-rose-50/95 rounded-lg ring-2 ring-rose-300 px-3 text-center">
                                <ShieldCheck className="w-10 h-10 text-rose-600 mb-2" />
                                <div className="text-base font-bold text-rose-800">QR hidden</div>
                                <div className="text-xs text-rose-700 mt-1">Sharing forbidden</div>
                            </div>
                        )}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-3 text-center">
                        Works with GPay · PhonePe · Paytm · BHIM
                    </div>
                </div>

                {/* Action bar: Download + Share. Both are hidden when
                    the merchant has turned off downloads, during hold
                    cover state, or during a screenshot shield event. */}
                {data.qr_allow_download && !qrHoldMode && !screenshotShield && (
                    <div className="w-full px-5 pb-5 flex items-center gap-2 border-t border-slate-100 pt-4">
                        <button
                            type="button"
                            data-testid="qr-zoom-download"
                            disabled={qrActionBusy === "download"}
                            onClick={handleDownload}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 active:scale-[0.98] transition-all text-[13px] font-medium disabled:opacity-50"
                        >
                            {qrActionBusy === "download"
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <Download className="w-4 h-4" />}
                            Download
                        </button>
                        <button
                            type="button"
                            data-testid="qr-zoom-share"
                            disabled={qrActionBusy === "share"}
                            onClick={handleShare}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98] transition-all text-[13px] font-semibold shadow-sm disabled:opacity-50"
                        >
                            {qrActionBusy === "share"
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <Share2 className="w-4 h-4" />}
                            Share QR
                        </button>
                    </div>
                )}

                {/* Tiny inline confirmation that fades out automatically. */}
                {qrShareNote && (
                    <div data-testid="qr-share-note"
                         className="absolute bottom-1.5 left-1/2 -translate-x-1/2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full shadow-sm animate-in fade-in slide-in-from-bottom-1">
                        {qrShareNote}
                    </div>
                )}

                {/* Bottom hint */}
                <div className="text-[10px] text-slate-400 pb-3 px-4 text-center">
                    Tap outside or press ESC to close · Screenshots protected
                </div>
            </div>
        </div>
    );
}
